// Jeden řádek menu — listová agenda (s tlačítkem osamostatnit) nebo skupina
// (počet + šipka). Stavy hover/open/selected dle prototypu `menu.css .phm-row`.

import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { Icon } from '@/shared/ui/icon';

import { LeadIcon } from './LeadIcon';
import { useMenuConfig } from './menuConfigContext';
import { leafCount } from './menuTreeUtils';
import type { MenuGroupNode, MenuItemNode } from './menuTypes';

export interface MenuRowProps {
  readonly node: MenuItemNode | MenuGroupNode;
  /** Vybraná skupina (drill-down sloupce) nebo vynuceně vybráno (spec). */
  readonly selected?: boolean;
  /** Vynucený hover (Storybook anatomie). */
  readonly forceHover?: boolean;
  /** Stopa cesty (breadcrumb rodičovských skupin) — výsledky fulltextu v `MenuSearch`. */
  readonly trail?: readonly string[];
  /** Klik do těla řádku. */
  readonly onActivate: () => void;
}

export function MenuRow({
  node,
  selected = false,
  forceHover = false,
  trail,
  onActivate,
}: MenuRowProps) {
  const { densityTokens: d, detachAlways, onDetach } = useMenuConfig();
  const { t } = useTranslation('sharedUi');
  const isGroup = node.t === 'group';
  // Externí odkaz (link) se otevírá v nové záložce → nedává smysl „osamostatnit do okna".
  const isLink = node.t === 'item' && node.url != null;

  return (
    <div
      className={cn(
        'phm-row group rounded-phx relative flex cursor-pointer items-center gap-2 transition-colors',
        d.rowMinH,
        d.rowPadY,
        'pr-1.5 pl-2',
        selected
          ? 'bg-phx-primary-tint'
          : forceHover
            ? 'bg-phx-surface-2'
            : 'hover:bg-phx-surface-2'
      )}
      onClick={onActivate}
    >
      <LeadIcon node={node} />

      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            'truncate leading-[1.25]',
            d.text,
            selected ? 'text-phx-primary-active font-semibold' : 'text-phx-text'
          )}
          // Tooltip s plným názvem JEN když je text useknutý (elipsis). Nastavujeme lazy na hover:
          // useknuto → title = plný název; jinak title odebereme, ať se ukáže hint z řádku.
          onMouseEnter={(e) => {
            const el = e.currentTarget;
            if (el.scrollWidth > el.clientWidth) el.title = node.title;
            else el.removeAttribute('title');
          }}
        >
          {node.title}
        </span>
        {trail && trail.length > 0 ? (
          <span className="text-phx-text-muted truncate text-[9.5px] leading-tight">
            {trail.join(' › ')}
          </span>
        ) : null}
      </span>

      {isGroup ? (
        <>
          <span className="text-phx-text-faint shrink-0 px-0.5 text-[9.5px] tabular-nums">
            {leafCount(node)}
          </span>
          <span className="text-phx-text-muted inline-flex shrink-0">
            <Icon name="chevright" size={11} />
          </span>
        </>
      ) : isLink ? null : (
        <button
          type="button"
          className={cn(
            'rounded-phx text-phx-text-muted inline-flex h-6 w-6 shrink-0 items-center justify-center',
            'border border-transparent bg-transparent transition-[opacity,background-color,color,border-color]',
            detachAlways ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            'group-hover:border-phx-border-2 group-hover:bg-phx-surface',
            'hover:!border-phx-primary hover:!bg-phx-primary-tint hover:!text-phx-primary-active'
          )}
          title={t('mainMenu.detachTitle')}
          onClick={(e) => {
            e.stopPropagation();
            onDetach(node);
          }}
        >
          <Icon name="detach" size={12} />
        </button>
      )}
    </div>
  );
}
